/**
 * enginePreProcessJson_Array.ts - Array processing methods for EnginePreProcessJson
 * Pure ES module - works in both Node.js and browser via bundler
 *
 * Uses new HtmlJson format with type prefixes:
 * s=Html, k=JsonKey, K=JsonKeySkip, p=Placeholder, P=PlaceholderSkip,
 * h=Slotholder, H=SlotholderSkip, a=ArrayBlock, A=ArrayBlockSkip, x=SlotHtml, i=Slot, c=Conditional
 */

import {
    hasKey,
    getValue,
    getKeys,
    getKeysList,
    convertToString,
    convertToBoolean,
    unwrapJsonValue
} from './enginePreProcessJson_Helper.js';
import { TemplateInfoNode, TemplateOperation } from './enginePreProcessJson_Helper.js';
import { TemplateOperationKind } from './enginePreProcessJson_Helper.js';
import {
    findPairedConditionalBlocks,
    selectConditionalContentForCondition
} from './enginePreProcessJson_Merge_Conditional.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { JsonArray, JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/index.js';

// Import constants from shared constants file
import {
    TYPE_HTML,
    TYPE_JSONKEY,
    TYPE_JSONKEY_SKIP,
    TYPE_ARRAYBLOCK,
    TYPE_SLOTHTML,
    TYPE_CONDITIONAL,
    META_KEY,
    isTypePrefixedKey
} from './enginePreProcessJson_Merge_Constants.js';

import type { EnginePreProcessJson, ILoaderPreProcessJson } from './enginePreProcessJson.js';
import type { PreProcessJsonMergeContext } from './enginePreProcessJson_Merge.js';
import { processArrayBlockEntry } from './enginePreProcessJson_Merge_ArrayBlock.js';

type PlainJsonObject = { [key: string]: unknown };

/**
 * Merge template function signature
 */
type MergeTemplateFn = (
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    templateInfo: TemplateInfoNode,
    htmlJson: JsonObject,
    dataJson: JsonObject,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null,
    recursionDepth: number,
    flowStack: string[] | null
) => string;

/**
 * Merges array block with data
 */
export function mergeArrayBlock(
    mergeTemplate: MergeTemplateFn,
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    arrayBlockData: JsonObject,
    arrayKey: string,
    dataJson: JsonObject,
    templateInfo: TemplateInfoNode,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null = null,
    recursionDepth: number = 0,
    flowStack: string[] | null = null
): string {
    const contextJson = context.contextJson;
    // Check if array key exists in dataJson (case-insensitive with plural matching)
    let actualKey: string | null = null;
    let arrayDataFromContext: JsonArray | null = null;
    let wasValidArrayFormat = false;
    if (dataJson) {
        for (const key of getKeys(dataJson)) {
            if (key.toLowerCase() === arrayKey.toLowerCase()) {
                actualKey = key;
                const keyValue = getValue(dataJson, key);
                if (Array.isArray(keyValue) || keyValue instanceof JsonArray) {
                    wasValidArrayFormat = true;
                }
                break;
            }
        }

        // Plural matching: try TrimEnd('s') on data keys
        if (actualKey === null) {
            for (const key of getKeys(dataJson)) {
                if (key.replace(/s+$/, '').toLowerCase() === arrayKey.toLowerCase()) {
                    actualKey = key;
                    const keyValue = getValue(dataJson, key);
                    if (Array.isArray(keyValue) || keyValue instanceof JsonArray) {
                        wasValidArrayFormat = true;
                    }
                    break;
                }
            }
        }
    }

    // Check contextJson for context arrays if:
    // 1. Key was not found in dataJson at all, OR
    // 2. Key was found but is not a valid array (e.g., it's a placeholder string)
    if (contextJson !== null && (!wasValidArrayFormat)) {
        for (const key of getKeys(contextJson)) {
            if (key.toLowerCase() === arrayKey.toLowerCase() ||
                key.replace(/s+$/, '').toLowerCase() === arrayKey.toLowerCase()) {
                const contextValue = getValue(contextJson, key);
                if (Array.isArray(contextValue) || contextValue instanceof JsonArray) {
                    // Convert array to list of JsonObjects
                    arrayDataFromContext = new JsonArray();
                    for (let item of contextValue) {
                        // Unwrap JsonValue if array contains JsonValue items
                        if (item instanceof JsonValue) {
                            item = unwrapJsonValue(item) as PlainJsonObject;
                        }
                        if (typeof item === 'object' && item !== null && !Array.isArray(item) && !(item instanceof JsonArray)) {
                            const obj = new JsonObject();
                            const itemEntries = item instanceof Map ? item.entries() : Object.entries(item as PlainJsonObject);
                            for (const [subKey, subValue] of itemEntries) {
                                obj.set(subKey, subValue as JsonValue);
                            }
                            arrayDataFromContext.push(obj as unknown as JsonValue);
                        } else {
                            const simpleObj = new JsonObject();
                            simpleObj.set('Value', item as JsonValue);
                            arrayDataFromContext.push(simpleObj as unknown as JsonValue);
                        }
                    }
                    actualKey = key; // Mark as found
                }
                break;
            }
        }
    }

    // A PAIRED array block ({{@Key}}/{{!@Key}} + {{^Key}}) whose data is not an array: evaluate the
    // key as a boolean (context priority, mirrors Normal's BuildMergedJson) so MainBlock renders
    // when true and NegativeBlock when false.
    // A STANDALONE {{^Key}} never reaches this processor — the parser classifies it as a conditional
    // block, matching Normal, where only keys with a {{@Key}} block are owned by the array processor.
    let resolvedAsBooleanCondition = false;
    let booleanConditionValue = false;
    if (actualKey !== null && !wasValidArrayFormat) {
        let condVal: unknown = undefined;
        let condFound = false;
        if (contextJson !== null && hasKey(contextJson, arrayKey)) {
            condVal = getValue(contextJson, arrayKey);
            condFound = true;
        } else if (hasKey(dataJson, actualKey)) {
            condVal = getValue(dataJson, actualKey);
            condFound = true;
        }
        if (condFound && !(Array.isArray(condVal) || condVal instanceof JsonArray)) {
            booleanConditionValue = convertToBoolean(condVal);
            resolvedAsBooleanCondition = true;
        }
    }

    // If array key is completely missing from JSON (not just empty), return DefaultReplaceHtml
    if (actualKey === null) {
        // Log ArrayIteration operation with "(array not found)" (matching C# behavior)
        if (operations && genOperations) {
            const templateKey = `${templateInfo.templateAppSite}_${(templateInfo as TemplateInfoNode).placeholderName || templateInfo.templateName}`;
            operations.push(new TemplateOperation({
                templateName: templateKey,
                kind: TemplateOperationKind.ArrayIteration,
                target: `{{@${arrayKey}}}`,
                replacedWith: `${loader.defaultReplaceHtml} (array not found)`,
                operations: []
            }));
        }
        return loader.defaultReplaceHtml;
    }

    // Get array data using actual key, or use context data if available
    const arrayData = arrayDataFromContext !== null ? arrayDataFromContext : getValue(dataJson, actualKey);

    // Boolean condition (resolved above): render MainBlock when true, NegativeBlock when false.
    if (resolvedAsBooleanCondition) {
        if (booleanConditionValue) {
            const mainBlockObj = arrayBlockData.get('MainBlock').asObject();
            if (mainBlockObj) {
                const childOperations: TemplateOperation[] = [];
                return mergeTemplate(
                    engine,
                    loader,
                    context,
                    templateInfo,
                    mainBlockObj,
                    dataJson,
                    childOperations,
                    genOperations,
                    componentHtml,
                    recursionDepth + 1,
                    flowStack
                );
            }
            return '';
        } else {
            const negBlockObjBool = arrayBlockData.get('NegativeBlock').asObject();
            if (negBlockObjBool) {
                return mergeNegativeBlock(
                    mergeTemplate,
                    engine,
                    loader,
                    context,
                    negBlockObjBool,
                    arrayKey,
                    dataJson,
                    templateInfo,
                    operations,
                    genOperations,
                    componentHtml,
                    recursionDepth,
                    flowStack
                );
            }
            return '';
        }
    }

    if (arrayData === null || arrayData === undefined) {
        // Key exists but value is empty/null - use negative block if available
        const negBlockObj1 = arrayBlockData.get('NegativeBlock').asObject();
        if (negBlockObj1) {
            return mergeNegativeBlock(
                mergeTemplate,
                engine,
                loader,
                context,
                negBlockObj1,
                arrayKey,
                dataJson,
                templateInfo,
                operations,
                genOperations,
                componentHtml,
                recursionDepth,
                flowStack
            );
        }
        return '';
    }

    // Non-array values are no longer handled here - {{@Key}} is arrays only
    // Boolean/int/string conditionals now use {{?Key}}

    // Check if array is already parsed as Array or JsonArray (from loader)
    // Match C# behavior: expect array to be pre-parsed by loader, not a string
    let arrayItems: unknown[] | null = null;
    if (Array.isArray(arrayData) || arrayData instanceof JsonArray) {
        arrayItems = arrayData as unknown[];
    } else {
        // Not a parsed array - return empty (match C# behavior)
        return '';
    }

    if (arrayItems === null || arrayItems.length === 0) {
        // Empty or invalid - use negative block
        const negBlockObj2 = arrayBlockData.get('NegativeBlock').asObject();
        if (negBlockObj2) {
            return mergeNegativeBlock(
                mergeTemplate,
                engine,
                loader,
                context,
                negBlockObj2,
                arrayKey,
                dataJson,
                templateInfo,
                operations,
                genOperations,
                componentHtml,
                recursionDepth,
                flowStack
            );
        }
        // No NegativeBlock - log ArrayIteration operation for empty array to match NormalJson behavior
        if (operations && genOperations) {
            const templateKey = `${templateInfo.templateAppSite}_${(templateInfo as TemplateInfoNode).placeholderName || templateInfo.templateName}`;
            const currentTemplateName = templateKey.includes('_') ? templateKey.substring(templateKey.indexOf('_') + 1) : templateKey;
            operations.push(new TemplateOperation({
                templateName: templateKey,
                kind: TemplateOperationKind.ArrayIteration,
                target: `{{@${arrayKey}}}`,
                replacedWith: `${currentTemplateName}_${actualKey} (0 items, 0 logged)`,
                operations: []
            }));
        }
        return '';
    }

    // Merge main block for each array item
    const result: string[] = [];
    const mainBlock = arrayBlockData.get('MainBlock').asObject() ?? new JsonObject();

    // Collect operations from first N items (matching C# behavior)
    const arrayOperations: TemplateOperation[] = [];
    const templateKey = `${templateInfo.templateAppSite}_${(templateInfo as TemplateInfoNode).placeholderName || templateInfo.templateName}`;

    for (let i = 0; i < arrayItems.length; i++) {
        let item = arrayItems[i];
        // Unwrap JsonValue if array contains JsonValue items (from JsonArray)
        if (item instanceof JsonValue) {
            if ((item as JsonValue).isObject) {
                item = unwrapJsonValue(item as JsonValue);
            } else {
                // Wrap scalar as {"Value": item} for uniform handling (matches C#)
                const simpleObj = new JsonObject();
                simpleObj.set('Value', item as JsonValue);
                item = simpleObj;
            }
        }
        const itemData: JsonObject = item instanceof JsonObject ? item : new JsonObject(typeof item === 'object' ? item as PlainJsonObject : { Value: item });

        // Create child operations for this iteration
        const itemOperations: TemplateOperation[] = [];

        // Pass templateKey with _ArrayItem suffix to match C#
        const itemTemplateKey = templateKey + '_ArrayItem';

        const itemMerged = mergeArrayItemContent(
            mergeTemplate,
            engine,
            loader,
            context,
            mainBlock,
            itemData,
            dataJson,
            templateInfo,
            itemOperations,
            genOperations,
            componentHtml,
            recursionDepth,
            itemTemplateKey,
            flowStack
        );
        result.push(itemMerged);

        // Only collect operations for the first N items (configured by maxArrayOperationItems)
        if (genOperations && i < engine.maxArrayOperationItems) {
            arrayOperations.push(...itemOperations);
        }
    }

    // Get current template name for source identifier (extract from templateKey)
    const currentTemplateName = templateKey.includes('_') ? templateKey.substring(templateKey.indexOf('_') + 1) : templateKey;

    // Log ArrayIteration operation with proper format matching C#
    const loggedCount = arrayItems.length < engine.maxArrayOperationItems ? arrayItems.length : engine.maxArrayOperationItems;
    if (operations && genOperations) {
        // Match Normal HTML engine's variant-matched ArrayIteration Target casing
        // (tries [as-is, lower, lower without trailing 's', lower+'s'] against the template literal).
        let targetKey = arrayKey;
        const keyNorm = actualKey!.toLowerCase();
        const variants = [actualKey!, keyNorm, keyNorm.replace(/s+$/, ''), keyNorm + 's'];
        for (const variant of variants) {
            if (variant.toLowerCase() === arrayKey.toLowerCase()) {
                targetKey = variant;
                break;
            }
        }
        operations.push(new TemplateOperation({
            templateName: templateKey,
            kind: TemplateOperationKind.ArrayIteration,
            target: `{{@${targetKey}}}`,
            replacedWith: `${currentTemplateName}_${actualKey} (${arrayItems.length} items, ${loggedCount} logged)`,
            operations: arrayOperations
        }));
    }

    return result.join('');
}

/**
 * Merges negative block ({{^ArrayKey}})
 * Matches C# MergeNegativeBlock - logs NegativeCondition operations
 */
export function mergeNegativeBlock(
    mergeTemplate: MergeTemplateFn,
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    negativeBlock: JsonObject,
    arrayKey: string,
    dataJson: JsonObject,
    templateInfo: TemplateInfoNode,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null = null,
    recursionDepth: number = 0,
    flowStack: string[] | null = null
): string {
    const templateKey = `${templateInfo.templateAppSite}_${(templateInfo as TemplateInfoNode).placeholderName || templateInfo.templateName}`;

    // Check if array exists and is non-empty (case-insensitive)
    let showContent = true;
    let actualKey: string | null = null;
    if (dataJson) {
        for (const key of getKeys(dataJson)) {
            if (key.toLowerCase() === arrayKey.toLowerCase()) {
                actualKey = key;
                break;
            }
        }
    }

    if (actualKey !== null) {
        const arrayValue = getValue(dataJson, actualKey);
        // Check if array is parsed and has items
        if ((Array.isArray(arrayValue) || arrayValue instanceof JsonArray) && (arrayValue as unknown[]).length > 0) {
            showContent = false; // Array exists and has items - don't show negative block
        }
    }

    if (showContent) {
        // Array is empty or doesn't exist - show the negative block content
        const childOperations: TemplateOperation[] = [];
        const merged = mergeTemplate(
            engine,
            loader,
            context,
            templateInfo,
            negativeBlock,
            dataJson,
            childOperations,
            genOperations,
            componentHtml,
            recursionDepth + 1,
            flowStack
        );

        // Log NegativeCondition operation
        if (operations && genOperations) {
            operations.push(new TemplateOperation({
                templateName: templateKey,
                kind: TemplateOperationKind.NegativeCondition,
                target: `{{^${actualKey ?? arrayKey}}}`,
                replacedWith: '(shown)',
                operations: childOperations
            }));
        }

        return merged;
    } else {
        // Array has items - don't show negative block
        if (operations && genOperations) {
            operations.push(new TemplateOperation({
                templateName: templateKey,
                kind: TemplateOperationKind.NegativeCondition,
                target: `{{^${actualKey ?? arrayKey}}}`,
                replacedWith: '(hidden)',
                operations: []
            }));
        }

        return '';
    }
}

/**
 * Merges array item content
 * Matches C# MergeArrayItemContent - specialized handling for array item KeyReplace
 */
export function mergeArrayItemContent(
    mergeTemplate: MergeTemplateFn,
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    mainBlock: JsonObject,
    itemData: JsonObject,
    parentDataJson: JsonObject,
    templateInfo: TemplateInfoNode,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null = null,
    recursionDepth: number = 0,
    templateKey: string | null = null,
    flowStack: string[] | null = null,
    processedKeys: Set<string> | null = null
): string {
    // Initialize processed keys for deduplication (matching C#)
    if (!processedKeys) {
        processedKeys = new Set<string>();
    }

    // Use provided templateKey or build from templateInfo
    if (!templateKey) {
        templateKey = `${templateInfo.templateAppSite}_${(templateInfo as TemplateInfoNode).placeholderName || templateInfo.templateName}_ArrayItem`;
    }

    const result: string[] = [];
    const content = mainBlock || {};
    const keysArray = getKeysList(getKeys(content));

    // Stage matched/missing keys during the substitution walk so KeyReplace ops can be
    // emitted in itemData-source order (matches Normal HTML engine's merged-data iteration).
    const matchedItemKeys = new Set<string>();    // lowercase keys matched in itemData
    const missingKeysInScanOrder: string[] = [];   // original-case missing keys, scan order

    for (const key of keysArray) {
        // Skip metadata
        if (key === META_KEY) continue;

        const value = getValue(content, key);

        // Get type from first character for type-prefixed keys
        const keyType = isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0';
        const keyNameFromKey = GenerateHtmlJson.getKeyName(key);

        if (keyType === TYPE_HTML || keyType === TYPE_SLOTHTML) {
            // Regular HTML content
            result.push(value as string);
        } else if (keyType === TYPE_JSONKEY || keyType === TYPE_JSONKEY_SKIP) {
            // Replace JSON key with value from current array item
            // In new format, the key name is in the key itself (e.g., "k2_Title" -> "Title")
            const keyName = keyNameFromKey;

            if (itemData && hasKey(itemData, keyName)) {
                result.push(convertToString(getValue(itemData, keyName)));
                matchedItemKeys.add(keyName.toLowerCase());
            } else {
                // Key not found - replace with DefaultReplaceHtml
                result.push(loader.defaultReplaceHtml);
                missingKeysInScanOrder.push(keyName);
            }
        } else if (keyType === TYPE_CONDITIONAL) {
            const conditionalBlockData = value as JsonObject;
            result.push(mergeArrayItemConditionalBlock(
                mergeTemplate,
                engine,
                loader,
                context,
                conditionalBlockData,
                keyNameFromKey,
                itemData,
                parentDataJson,
                templateInfo,
                operations,
                genOperations,
                componentHtml,
                recursionDepth,
                templateKey,
                flowStack,
                processedKeys
            ));
        } else if (keyType === TYPE_ARRAYBLOCK) {
            // Nested array block ({{@Key}} inside an array item) — expand a template-declared
            // nested array using this item's data, via the same metadata-driven array processor
            // as the top level. The nested array's data lives in itemData, so it is passed as
            // dataJson here. Op attribution flows from templateInfo (the component, e.g. UserList),
            // not the synthetic "_ArrayItem" item context — matches NormalJson's result.
            const nestedContent = value instanceof JsonObject ? value : (typeof value === 'object' && value !== null ? new JsonObject(value as PlainJsonObject) : null);
            if (nestedContent) {
                result.push(processArrayBlockEntry(
                    mergeTemplate,
                    engine,
                    loader,
                    context,
                    key,
                    nestedContent,
                    templateInfo,
                    itemData,
                    operations,
                    genOperations,
                    componentHtml,
                    recursionDepth,
                    flowStack
                ));
            }
        } else if (typeof value === 'object' && value !== null) {
            // Nested object - recurse
            const nestedObj = value instanceof JsonObject ? value : new JsonObject(value as PlainJsonObject);
            const nested = mergeArrayItemContent(
                mergeTemplate,
                engine,
                loader,
                context,
                nestedObj,
                itemData,
                parentDataJson,
                templateInfo,
                operations,
                genOperations,
                componentHtml,
                recursionDepth,
                templateKey,
                flowStack,
                processedKeys
            );
            result.push(nested);
        } else {
            result.push(value as string);
        }
    }

    // Emit KeyReplace ops in itemData order (matches Normal's merged-data iteration),
    // then KeyMissing in template scan order.
    if (genOperations) {
        for (const itemKey of getKeys(itemData)) {
            const itemKeyLower = itemKey.toLowerCase();
            if (matchedItemKeys.has(itemKeyLower) && !processedKeys.has(itemKeyLower)) {
                processedKeys.add(itemKeyLower);
                operations.push(new TemplateOperation({
                    templateName: templateKey,
                    kind: TemplateOperationKind.KeyReplace,
                    target: `{{$${itemKey}}}`,
                    replacedWith: 'ArrayItem_' + itemKey,
                    operations: []
                }));
            }
        }
        for (const missingKey of missingKeysInScanOrder) {
            const missingKeyLower = missingKey.toLowerCase();
            if (!processedKeys.has(missingKeyLower)) {
                processedKeys.add(missingKeyLower);
                operations.push(new TemplateOperation({
                    templateName: templateKey,
                    kind: TemplateOperationKind.KeyMissing,
                    target: `{{$${missingKey}}}`,
                    replacedWith: templateInfo.templateName + '_' + missingKey,
                    operations: []
                }));
            }
        }
    }

    return result.join('');
}

function mergeArrayItemConditionalBlock(
    mergeTemplate: MergeTemplateFn,
    engine: EnginePreProcessJson,
    loader: ILoaderPreProcessJson,
    context: PreProcessJsonMergeContext,
    conditionalBlockData: JsonObject,
    fallbackConditionalKey: string,
    itemData: JsonObject,
    parentDataJson: JsonObject,
    templateInfo: TemplateInfoNode,
    operations: TemplateOperation[],
    genOperations: boolean,
    componentHtml: JsonObject | null,
    recursionDepth: number,
    templateKey: string,
    flowStack: string[] | null,
    processedKeys: Set<string>
): string {
    const conditionalKey = (getValue(conditionalBlockData, 'ConditionalKey') as string | null) || fallbackConditionalKey;
    let conditionMet = engine.evaluateCondition(conditionalKey, itemData);
    if (getValue(conditionalBlockData, 'IsNegative')) {
        conditionMet = !conditionMet;
    }

    const { positiveContent, negativeContent } = findPairedConditionalBlocks(conditionalBlockData);
    const selectedContent = selectConditionalContentForCondition(conditionMet, positiveContent, negativeContent);

    if (!selectedContent) {
        return '';
    }

    return mergeArrayItemContent(
        mergeTemplate,
        engine,
        loader,
        context,
        selectedContent,
        itemData,
        parentDataJson,
        templateInfo,
        operations,
        genOperations,
        componentHtml,
        recursionDepth,
        templateKey,
        flowStack,
        processedKeys
    );
}
